import React from 'react';
import { POPUP_INDEX } from '../../../styles/constants/priority';
import ModalBase from './ModalBase';
import styled from '@emotion/styled';
import Title from '../text/Title';
import { SimpleItem } from '../../../types/common/modal/Item';
import SimpleListContents from './contents/SimpleListContents';
import useSwitching from '../../../hooks/common/useSwitching';

const Base = styled.div`
  width: auto;
  height: auto;
`;

interface Props<T> {
  title: string;
  list: SimpleItem<T>[];
  selectedNo: number;
  active: boolean;
  clickPriority?: boolean;
  itemMaxWidth?: string;
  justifySelf?: string;
  prevEvent?: () => void;
  closeEvent: () => void;
  onClick: (param: any) => void;
}

/**
 *  심플한 목록을 보여주는 공통 모달 컴포넌트
 * @param {string} title 모달 제목
 * @param {SimpleItem[]} list 모달에 나올 아이템 목록
 * @param {number} selectedNo 선택된 아이템 번호
 * @param {boolean} active 모달 활성화 여부
 * @param {boolean} clickPriority 클릭 우선 여부 (default: false)
 * @param {string} itemMaxWidth 옵션 하나의 로우 max-width (default: auto)
 * @param {string} justifySelf item 배치 방향( start, center, end, default: center)
 * @param {function} prevEvent 모달 뒤로가기 이벤트 함수
 * @param {function} closeEvent 모달 닫기 이벤트 함수
 *  */
function SimpleListModal<T>({
  title,
  list,
  selectedNo,
  active,
  clickPriority = false,
  itemMaxWidth = 'auto',
  justifySelf,
  prevEvent,
  closeEvent,
  onClick,
}: Props<T>): JSX.Element {
  const [transitionTrigger, switchingTransitionTrigger] = useSwitching(true);
  const clickEvent = <T extends unknown>(param: T, id?: number): void => {
    switchingTransitionTrigger();

    clickPriority ? onClick(param) : selectedNo !== id && onClick(param);
  };

  return (
    <ModalBase
      active={active}
      zIndex={POPUP_INDEX}
      prevEvent={prevEvent}
      closeEvent={closeEvent}
      subTrigger={transitionTrigger}
    >
      <Base>
        <Title text={title} />
        <SimpleListContents
          list={list}
          selectedNo={selectedNo}
          itemMaxWidth={itemMaxWidth}
          justifySelf={justifySelf}
          onClick={clickEvent}
        />
      </Base>
    </ModalBase>
  );
}

export default React.memo(SimpleListModal);
